"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getValueAtPath = getValueAtPath;
exports.allowTemplateVariables = allowTemplateVariables;
exports.validateArgsWithVariables = validateArgsWithVariables;
exports.validateEdgeConditionVariables = validateEdgeConditionVariables;
exports.buildWorkflowVariableRegistry = buildWorkflowVariableRegistry;
exports.getAvailableVariablesAtNode = getAvailableVariablesAtNode;
exports.extractVariablePathsFromSchema = extractVariablePathsFromSchema;
exports.getToolOutputVariables = getToolOutputVariables;
exports.buildEnhancedWorkflowVariableRegistry = buildEnhancedWorkflowVariableRegistry;
exports.validateWorkflowVariables = validateWorkflowVariables;
exports.validateNodeInputVariables = validateNodeInputVariables;
exports.validateNodeEdgeConditionVariables = validateNodeEdgeConditionVariables;
exports.validateVariablePath = validateVariablePath;
exports.testComprehensiveValidation = testComprehensiveValidation;
var zod_1 = require("zod");
// Template variable pattern - matches {{variableName}} anywhere in string
var TEMPLATE_VAR_REGEX = /\{\{([^}]+)\}\}/g;
// Helper function to get value at a specific path
function getValueAtPath(obj, path) {
    var current = obj;
    for (var _i = 0, path_1 = path; _i < path_1.length; _i++) {
        var key = path_1[_i];
        if (current == null)
            return undefined;
        current = current[key];
    }
    return current;
}
/**
 * Extract all template variables from a string
 * @param str - String that may contain template variables like "{{userType}} == 'premium'"
 * @returns Array of variable names (without the {{}} syntax)
 */
function extractTemplateVariables(str) {
    var variables = [];
    var match;
    // Reset regex index to ensure fresh start
    TEMPLATE_VAR_REGEX.lastIndex = 0;
    while ((match = TEMPLATE_VAR_REGEX.exec(str)) !== null) {
        var varPath = match[1].trim();
        if (varPath) {
            variables.push(varPath);
        }
    }
    return variables;
}
// Template variable pattern - matches {{variableName}} or {{nodeId.field}}
var templateVariableSchema = zod_1.z
    .string()
    .regex(/^\{\{[^}]+\}\}$/, 'Must be a valid template variable like {{variableName}}');
// String pattern that allows template variables embedded anywhere within the string
var stringWithTemplateVariableSchema = zod_1.z
    .string()
    .refine(function (value) { return /\{\{[^}]+\}\}/.test(value); }, 'Must contain a template variable like {{variableName}}');
/**
 * Recursively transform a Zod schema to allow template variables at any point
 * @param schema - The Zod schema to transform
 * @param discriminatorKey - Optional discriminator key to preserve in discriminated unions
 * @param insideDiscriminatedUnion - Whether we're processing an option inside a discriminated union (to avoid double-wrapping)
 */
function allowTemplateVariables(schema, discriminatorKey, insideDiscriminatedUnion) {
    var _a, _b;
    if (insideDiscriminatedUnion === void 0) { insideDiscriminatedUnion = false; }
    var typeName = (_a = schema._def) === null || _a === void 0 ? void 0 : _a.typeName;
    // If it's already a union that includes template variables, return as-is
    if (schema instanceof zod_1.z.ZodUnion || typeName === 'ZodUnion') {
        var options = schema.options.map(function (option) {
            return allowTemplateVariables(option, discriminatorKey);
        });
        return zod_1.z.union(options);
    }
    // Handle discriminated unions by recursing into each option
    // Note: ZodDiscriminatedUnion stores options in _def.options (a Map)
    // and discriminator in _def.discriminator
    if (('ZodDiscriminatedUnion' in zod_1.z && schema instanceof zod_1.z.ZodDiscriminatedUnion) ||
        typeName === 'ZodDiscriminatedUnion') {
        var def = schema._def;
        if (!def || typeof def.discriminator !== 'string') {
            console.log('[Validation] ZodDiscriminatedUnion missing _def or discriminator:', schema);
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        var discriminator_1 = def.discriminator;
        var rawOptions = def.options;
        var optionsArray = Array.isArray(rawOptions)
            ? rawOptions
            : typeof (rawOptions === null || rawOptions === void 0 ? void 0 : rawOptions.values) === 'function'
                ? Array.from(rawOptions.values())
                : [];
        if (optionsArray.length === 0) {
            console.log('[Validation] ZodDiscriminatedUnion has no options:', schema);
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        // Pass the discriminator key and flag to preserve objects without wrapping
        var mapped = optionsArray.map(function (opt) {
            return allowTemplateVariables(opt, discriminator_1, true);
        });
        // Use a regular union of all enhanced options plus template variable schema
        // (instead of trying to recreate a discriminated union which can lose enhancements)
        if (mapped.length >= 2) {
            var first = mapped[0], second = mapped[1], rest = mapped.slice(2);
            return zod_1.z.union(__spreadArray(__spreadArray([first, second], rest, true), [templateVariableSchema], false));
        }
        if (mapped.length === 1) {
            return zod_1.z.union([mapped[0], templateVariableSchema]);
        }
        // Final fallback
        return zod_1.z.union([schema, templateVariableSchema]);
    }
    // Handle objects by recursing into properties
    if (schema instanceof zod_1.z.ZodObject || typeName === 'ZodObject') {
        var shape = schema.shape;
        var newShape = {};
        for (var _i = 0, _c = Object.entries(shape); _i < _c.length; _i++) {
            var _d = _c[_i], key = _d[0], value = _d[1];
            // Allow template variables in discriminator fields as well
            // Templates need to use variables like {{adsetAction}} for discriminator values
            if (discriminatorKey && key === discriminatorKey) {
                // Allow either the literal value OR a template variable for the discriminator
                newShape[key] = zod_1.z.union([value, templateVariableSchema]);
            }
            else {
                // Don't pass discriminatorKey to nested fields - it only applies to immediate children
                // Don't pass insideDiscriminatedUnion to nested fields - only affects the immediate object
                newShape[key] = allowTemplateVariables(value, undefined, false);
            }
        }
        var newSchema = zod_1.z.object(newShape);
        // If inside a discriminated union, return the object directly without wrapping
        // The discriminated union handler will wrap the entire union with templateVariableSchema
        if (insideDiscriminatedUnion) {
            if (schema.isOptional()) {
                return newSchema.optional();
            }
            if (schema.isNullable()) {
                return newSchema.nullable();
            }
            return newSchema;
        }
        // For ZodObjects, we don't wrap in a union with templateVariableSchema because:
        // 1. Template variables are strings, not objects
        // 2. Objects with template variables in fields should be validated by the enhanced object schema
        // 3. Wrapping in a union causes objects to fail validation (they don't match the string templateVariableSchema)
        // The enhanced object schema (newSchema) already allows template variables in individual fields
        // Preserve original object schema properties
        if (schema.isOptional()) {
            return newSchema.optional();
        }
        if (schema.isNullable()) {
            return newSchema.nullable();
        }
        return newSchema;
    }
    // Handle arrays by recursing into element type
    if (schema instanceof zod_1.z.ZodArray || typeName === 'ZodArray') {
        if (!schema._def || !schema._def.type) {
            console.log('[Validation] ZodArray missing _def or type:', schema);
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        var elementSchema = allowTemplateVariables(schema._def.type, discriminatorKey);
        var newSchema = zod_1.z.array(elementSchema);
        // Allow either the enhanced array schema OR a template variable for the entire array
        var arrayOrTemplateSchema = zod_1.z.union([newSchema, templateVariableSchema]);
        if (schema.isOptional()) {
            return arrayOrTemplateSchema.optional();
        }
        if (schema.isNullable()) {
            return arrayOrTemplateSchema.nullable();
        }
        return arrayOrTemplateSchema;
    }
    // Handle records by recursing into value type
    if (schema instanceof zod_1.z.ZodRecord || typeName === 'ZodRecord') {
        if (!schema._def || !schema._def.valueType) {
            console.log('[Validation] ZodRecord missing _def or valueType:', schema);
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        var valueSchema = allowTemplateVariables(schema._def.valueType, discriminatorKey);
        var newSchema = zod_1.z.record(valueSchema);
        // Allow either the enhanced record schema OR a template variable for the entire record
        var recordOrTemplateSchema = zod_1.z.union([newSchema, templateVariableSchema]);
        if (schema.isOptional()) {
            return recordOrTemplateSchema.optional();
        }
        if (schema.isNullable()) {
            return recordOrTemplateSchema.nullable();
        }
        return recordOrTemplateSchema;
    }
    // Handle tuples by recursing into each item (and rest if present)
    if (schema instanceof zod_1.z.ZodTuple || typeName === 'ZodTuple') {
        var items = schema.items.map(function (i) {
            return allowTemplateVariables(i, discriminatorKey);
        });
        // @ts-expect-error: access rest safely if present
        var rest = schema.rest;
        var base = zod_1.z.tuple(items);
        var newSchema = rest ? base.rest(allowTemplateVariables(rest, discriminatorKey)) : base;
        // Allow either the enhanced tuple schema OR a template variable for the entire tuple
        var tupleOrTemplateSchema = zod_1.z.union([newSchema, templateVariableSchema]);
        return tupleOrTemplateSchema;
    }
    // Handle sets by recursing into element type
    if (schema instanceof zod_1.z.ZodSet || typeName === 'ZodSet') {
        if (!schema._def || !schema._def.valueType) {
            console.log('[Validation] ZodSet missing _def or valueType:', schema);
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        var elementSchema = allowTemplateVariables(schema._def.valueType, discriminatorKey);
        var newSchema = zod_1.z.set(elementSchema);
        // Allow either the enhanced set schema OR a template variable for the entire set
        var setOrTemplateSchema = zod_1.z.union([newSchema, templateVariableSchema]);
        if (schema.isOptional()) {
            return setOrTemplateSchema.optional();
        }
        if (schema.isNullable()) {
            return setOrTemplateSchema.nullable();
        }
        return setOrTemplateSchema;
    }
    // Handle maps by recursing into value type
    if (schema instanceof zod_1.z.ZodMap || typeName === 'ZodMap') {
        if (!schema._def || !schema._def.keyType || !schema._def.valueType) {
            console.log('[Validation] ZodMap missing _def, keyType, or valueType:', schema);
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        var keySchema = allowTemplateVariables(schema._def.keyType, discriminatorKey);
        var valueSchema = allowTemplateVariables(schema._def.valueType, discriminatorKey);
        var newSchema = zod_1.z.map(keySchema, valueSchema);
        // Allow either the enhanced map schema OR a template variable for the entire map
        var mapOrTemplateSchema = zod_1.z.union([newSchema, templateVariableSchema]);
        if (schema.isOptional()) {
            return mapOrTemplateSchema.optional();
        }
        if (schema.isNullable()) {
            return mapOrTemplateSchema.nullable();
        }
        return mapOrTemplateSchema;
    }
    // Handle ZodEffects (created by .refine(), .superRefine(), etc.)
    // For ZodEffects, we enhance the inner schema to allow template variables in fields.
    // We don't wrap in a union with templateVariableSchema because:
    // - If inner is an object, template variables are strings not objects
    // - The enhanced inner schema already handles template variables in fields
    // - The refine effects will be bypassed when template variables are resolved at runtime
    if (schema instanceof zod_1.z.ZodEffects || typeName === 'ZodEffects') {
        var innerSchema = (_b = schema._def) === null || _b === void 0 ? void 0 : _b.schema;
        if (innerSchema) {
            // Recursively apply allowTemplateVariables to the inner schema
            // This allows template variables in individual fields
            var enhancedInner = allowTemplateVariables(innerSchema, discriminatorKey, insideDiscriminatedUnion);
            // Return the enhanced inner schema directly
            // The refine effects are preserved in the original schema, but we use the enhanced
            // inner to allow template variables. At runtime, template variables will be resolved
            // before the refine checks run.
            return enhancedInner;
        }
        // Fallback if we can't unwrap - return original schema (can't enhance without inner)
        return schema;
    }
    // Effects/default/lazy/intersection/enum/literal: allow template var via union fallback
    // These types either transform/brand or are complex; unwrapping risks losing behavior.
    // Allowing a template variable string alongside the original schema is sufficient.
    // Special handling for strings: allow embedded template variables anywhere in the string
    if (schema instanceof zod_1.z.ZodString || typeName === 'ZodString') {
        // Check if this string has specific validation (URL, email, etc.)
        var checks = schema._def.checks || [];
        var hasFormatValidation = checks.some(function (c) { return c.kind === 'url' || c.kind === 'email' || c.kind === 'uuid' || c.kind === 'cuid'; });
        if (hasFormatValidation) {
            // For strings with format validation, allow:
            // 1. Original schema (valid URL/email/etc)
            // 2. Pure template variables like {{nodeId.field}}
            // 3. Strings with embedded template variables like "https://example.com/{{path}}"
            // Put template schemas first so they match before strict format validation
            return zod_1.z.union([templateVariableSchema, stringWithTemplateVariableSchema, schema]);
        }
        // For regular strings, allow template variables
        return zod_1.z.union([templateVariableSchema, stringWithTemplateVariableSchema, schema]);
    }
    // Handle optional and nullable types
    if (schema instanceof zod_1.z.ZodOptional || typeName === 'ZodOptional') {
        return allowTemplateVariables(schema.unwrap(), discriminatorKey).optional();
    }
    if (schema instanceof zod_1.z.ZodNullable || typeName === 'ZodNullable') {
        return allowTemplateVariables(schema.unwrap(), discriminatorKey).nullable();
    }
    // Special case: If this is a literal type (like z.literal('raw')) and it's being used
    // as a discriminator, we should preserve it as-is to maintain discriminated union integrity
    if (schema instanceof zod_1.z.ZodLiteral || typeName === 'ZodLiteral') {
        // If we're not in a discriminated union context or this isn't the discriminator,
        // allow template variables
        if (!discriminatorKey) {
            return zod_1.z.union([schema, templateVariableSchema]);
        }
        // Otherwise preserve the literal as-is
        return schema;
    }
    // Special handling for enums: put template variable first so it matches before enum validation
    // Check typeName since instanceof doesn't work across module boundaries
    if (schema.constructor.name === 'ZodEnum' ||
        schema.constructor.name === 'ZodNativeEnum' ||
        typeName === 'ZodEnum' ||
        typeName === 'ZodNativeEnum') {
        // Put templateVariableSchema first so template variables are matched before strict enum validation
        return zod_1.z.union([templateVariableSchema, schema]);
    }
    // For primitive types and other types, create union with template variable
    return zod_1.z.union([schema, templateVariableSchema]);
}
// ============================================================================
// INPUT-AWARE TEMPLATE VARIABLE VALIDATION
// ============================================================================
// The following functions implement a smarter approach to template variable
// validation that makes decisions based on actual input values, not just schema
// structure. This allows us to:
// 1. Relax only fields that actually contain template variables
// 2. Preserve full type validation for non-template fields
// 3. Handle discriminated unions correctly when discriminator has template variable
/**
 * Check if a value contains a template variable (recursively)
 * @param value - Any value to check for template variables
 * @returns true if the value contains a template variable at any level
 */
function containsTemplateVariable(value) {
    if (typeof value === 'string') {
        // Use a fresh regex each time to avoid lastIndex issues
        return /\{\{[^}]+\}\}/.test(value);
    }
    if (Array.isArray(value)) {
        return value.some(containsTemplateVariable);
    }
    if (value !== null && typeof value === 'object') {
        return Object.values(value).some(containsTemplateVariable);
    }
    return false;
}
/**
 * Check if a value is directly a template variable (the entire string is a template var)
 * @param value - Any value to check
 * @returns true if the entire value is a single template variable
 */
function isTemplateVariable(value) {
    if (typeof value !== 'string')
        return false;
    return /^\{\{[^}]+\}\}$/.test(value);
}
/**
 * Relax a schema for a field that contains template variables
 * @param schema - The Zod schema to relax
 * @param input - The actual input value (used to preserve structure validation)
 * @param path - The current path for debugging
 * @returns A relaxed schema that accepts the input while preserving structure
 */
function relaxFieldForTemplateVariable(schema, input, path) {
    if (path === void 0) { path = []; }
    // If the entire input is a template variable string, accept any string
    if (isTemplateVariable(input)) {
        // Accept template variable strings
        return zod_1.z.union([templateVariableSchema, stringWithTemplateVariableSchema, schema]);
    }
    // If input is an object with template variables in fields, recursively relax
    if (input !== null && typeof input === 'object' && !Array.isArray(input)) {
        return relaxSchemaForTemplateVariables(schema, input, path);
    }
    // If input is an array with template variables, relax element validation
    if (Array.isArray(input)) {
        return relaxSchemaForTemplateVariables(schema, input, path);
    }
    // For strings with embedded template variables (like "Hello {{name}}")
    if (typeof input === 'string' && containsTemplateVariable(input)) {
        return zod_1.z.union([templateVariableSchema, stringWithTemplateVariableSchema, schema]);
    }
    // Fallback: accept template variables or the original schema
    return zod_1.z.union([templateVariableSchema, schema]);
}
/**
 * Recursively relax a schema based on the actual input values
 * Only relaxes fields that contain template variables, preserving
 * full validation for non-template fields.
 *
 * @param schema - The Zod schema to relax
 * @param input - The actual input value
 * @param path - The current path (for debugging)
 * @returns A schema that accepts template variables where needed
 */
function relaxSchemaForTemplateVariables(schema, input, path) {
    var _a, _b, _c, _d;
    if (path === void 0) { path = []; }
    var typeName = (_a = schema._def) === null || _a === void 0 ? void 0 : _a.typeName;
    // Handle undefined/null inputs - return original schema (will fail required check)
    if (input === undefined || input === null) {
        return schema;
    }
    // Handle discriminated unions specially
    if (('ZodDiscriminatedUnion' in zod_1.z && schema instanceof zod_1.z.ZodDiscriminatedUnion) ||
        typeName === 'ZodDiscriminatedUnion') {
        var def = schema._def;
        if (!def || typeof def.discriminator !== 'string') {
            return schema;
        }
        var discriminator_2 = def.discriminator;
        var discriminatorValue_1 = input === null || input === void 0 ? void 0 : input[discriminator_2];
        var rawOptions = def.options;
        var optionsArray = Array.isArray(rawOptions)
            ? rawOptions
            : typeof (rawOptions === null || rawOptions === void 0 ? void 0 : rawOptions.values) === 'function'
                ? Array.from(rawOptions.values())
                : [];
        if (optionsArray.length === 0) {
            return schema;
        }
        // If discriminator has template variable, validate against union of ALL variants
        // This allows structure validation without knowing which variant will be selected at runtime
        if (containsTemplateVariable(discriminatorValue_1)) {
            // Relax all variants and create a union
            var relaxedVariants_1 = optionsArray.map(function (variant) {
                return relaxSchemaForTemplateVariables(variant, input, __spreadArray(__spreadArray([], path, true), ['[variant]'], false));
            });
            if (relaxedVariants_1.length >= 2) {
                var first = relaxedVariants_1[0], second = relaxedVariants_1[1], rest = relaxedVariants_1.slice(2);
                return zod_1.z.union(__spreadArray([first, second], rest, true));
            }
            if (relaxedVariants_1.length === 1) {
                return relaxedVariants_1[0];
            }
            return schema;
        }
        // Discriminator has a concrete value - find matching variant and relax it
        var matchingVariant = optionsArray.find(function (variant) {
            try {
                var variantDef = variant._def;
                if ((variantDef === null || variantDef === void 0 ? void 0 : variantDef.typeName) === 'ZodObject' && variantDef.shape) {
                    var shape = typeof variantDef.shape === 'function' ? variantDef.shape() : variantDef.shape;
                    var discField = shape[discriminator_2];
                    if (discField) {
                        var discDef = discField._def;
                        if ((discDef === null || discDef === void 0 ? void 0 : discDef.typeName) === 'ZodLiteral') {
                            return discDef.value === discriminatorValue_1;
                        }
                    }
                }
            }
            catch (_a) {
                // Ignore parsing errors
            }
            return false;
        });
        if (matchingVariant) {
            return relaxSchemaForTemplateVariables(matchingVariant, input, path);
        }
        // No matching variant found - relax all variants as fallback
        var relaxedVariants = optionsArray.map(function (variant) {
            return relaxSchemaForTemplateVariables(variant, input, __spreadArray(__spreadArray([], path, true), ['[variant]'], false));
        });
        if (relaxedVariants.length >= 2) {
            var first = relaxedVariants[0], second = relaxedVariants[1], rest = relaxedVariants.slice(2);
            return zod_1.z.union(__spreadArray([first, second], rest, true));
        }
        return schema;
    }
    // Handle regular unions
    if (schema instanceof zod_1.z.ZodUnion || typeName === 'ZodUnion') {
        var options = schema.options;
        // Relax each option in the union
        var relaxedOptions = options.map(function (opt) { return relaxSchemaForTemplateVariables(opt, input, path); });
        if (relaxedOptions.length >= 2) {
            var first = relaxedOptions[0], second = relaxedOptions[1], rest = relaxedOptions.slice(2);
            return zod_1.z.union(__spreadArray([first, second], rest, true));
        }
        return relaxedOptions[0] || schema;
    }
    // Handle objects - walk through each field
    if (schema instanceof zod_1.z.ZodObject || typeName === 'ZodObject') {
        var shape = schema.shape;
        var inputObj = input;
        var newShape = {};
        for (var _i = 0, _e = Object.entries(shape); _i < _e.length; _i++) {
            var _f = _e[_i], key = _f[0], fieldSchema = _f[1];
            var fieldValue = inputObj === null || inputObj === void 0 ? void 0 : inputObj[key];
            var fieldPath = __spreadArray(__spreadArray([], path, true), [key], false);
            // If field contains template variables, relax it
            if (containsTemplateVariable(fieldValue)) {
                newShape[key] = relaxFieldForTemplateVariable(fieldSchema, fieldValue, fieldPath);
            }
            else {
                // No template variables - use original schema for full validation
                // But still recurse in case there are nested structures
                if (fieldValue !== undefined && fieldValue !== null && typeof fieldValue === 'object') {
                    newShape[key] = relaxSchemaForTemplateVariables(fieldSchema, fieldValue, fieldPath);
                }
                else {
                    newShape[key] = fieldSchema;
                }
            }
        }
        var newSchema = zod_1.z.object(newShape);
        // Preserve optional/nullable modifiers
        if (schema.isOptional()) {
            return newSchema.optional();
        }
        if (schema.isNullable()) {
            return newSchema.nullable();
        }
        return newSchema;
    }
    // Handle arrays
    if (schema instanceof zod_1.z.ZodArray || typeName === 'ZodArray') {
        if (!Array.isArray(input)) {
            // Input is not an array - if it's a template variable, allow it
            if (isTemplateVariable(input)) {
                return zod_1.z.union([templateVariableSchema, schema]);
            }
            return schema;
        }
        var elementSchema = (_b = schema._def) === null || _b === void 0 ? void 0 : _b.type;
        if (!elementSchema) {
            return schema;
        }
        // Check if any element contains template variables
        var hasTemplateVars = input.some(containsTemplateVariable);
        if (hasTemplateVars) {
            // Relax element schema for all elements (we can't create per-element schemas)
            // Find an element with template vars to use as reference for relaxation
            var templateElement = input.find(containsTemplateVariable) || input[0];
            var relaxedElementSchema = relaxSchemaForTemplateVariables(elementSchema, templateElement, __spreadArray(__spreadArray([], path, true), [
                '[element]',
            ], false));
            var relaxedArraySchema = zod_1.z.array(relaxedElementSchema);
            return zod_1.z.union([templateVariableSchema, relaxedArraySchema]);
        }
        // No template variables in array - validate normally
        return schema;
    }
    // Handle ZodEffects (created by .refine(), .superRefine(), etc.)
    if (schema instanceof zod_1.z.ZodEffects || typeName === 'ZodEffects') {
        var innerSchema = (_c = schema._def) === null || _c === void 0 ? void 0 : _c.schema;
        if (innerSchema) {
            // Relax the inner schema
            return relaxSchemaForTemplateVariables(innerSchema, input, path);
        }
        return schema;
    }
    // Handle optional types
    if (schema instanceof zod_1.z.ZodOptional || typeName === 'ZodOptional') {
        var innerSchema = schema.unwrap();
        return relaxSchemaForTemplateVariables(innerSchema, input, path).optional();
    }
    // Handle nullable types
    if (schema instanceof zod_1.z.ZodNullable || typeName === 'ZodNullable') {
        var innerSchema = schema.unwrap();
        return relaxSchemaForTemplateVariables(innerSchema, input, path).nullable();
    }
    // Handle literals - if input is a template variable, allow it
    if (schema instanceof zod_1.z.ZodLiteral || typeName === 'ZodLiteral') {
        if (isTemplateVariable(input)) {
            return zod_1.z.union([templateVariableSchema, schema]);
        }
        return schema;
    }
    // Handle enums - if input is a template variable, allow it
    if (schema.constructor.name === 'ZodEnum' ||
        schema.constructor.name === 'ZodNativeEnum' ||
        typeName === 'ZodEnum' ||
        typeName === 'ZodNativeEnum') {
        if (isTemplateVariable(input)) {
            return zod_1.z.union([templateVariableSchema, schema]);
        }
        return schema;
    }
    // Handle strings - if contains template variable, allow it
    if (schema instanceof zod_1.z.ZodString || typeName === 'ZodString') {
        if (containsTemplateVariable(input)) {
            return zod_1.z.union([templateVariableSchema, stringWithTemplateVariableSchema, schema]);
        }
        return schema;
    }
    // Handle numbers - if input is a template variable string, allow it
    if (schema instanceof zod_1.z.ZodNumber || typeName === 'ZodNumber') {
        if (isTemplateVariable(input)) {
            return zod_1.z.union([templateVariableSchema, schema]);
        }
        return schema;
    }
    // Handle booleans - if input is a template variable string, allow it
    if (schema instanceof zod_1.z.ZodBoolean || typeName === 'ZodBoolean') {
        if (isTemplateVariable(input)) {
            return zod_1.z.union([templateVariableSchema, schema]);
        }
        return schema;
    }
    // Handle records
    if (schema instanceof zod_1.z.ZodRecord || typeName === 'ZodRecord') {
        if (isTemplateVariable(input)) {
            return zod_1.z.union([templateVariableSchema, schema]);
        }
        if (input !== null && typeof input === 'object' && containsTemplateVariable(input)) {
            // If record has template variables in values, relax value schema
            var valueSchema = (_d = schema._def) === null || _d === void 0 ? void 0 : _d.valueType;
            if (valueSchema) {
                var sampleValue = Object.values(input).find(containsTemplateVariable);
                var relaxedValueSchema = relaxSchemaForTemplateVariables(valueSchema, sampleValue, __spreadArray(__spreadArray([], path, true), [
                    '[value]',
                ], false));
                var relaxedRecordSchema = zod_1.z.record(relaxedValueSchema);
                return zod_1.z.union([templateVariableSchema, relaxedRecordSchema]);
            }
        }
        return schema;
    }
    // For other types, if input contains template variable, allow it
    if (containsTemplateVariable(input)) {
        return zod_1.z.union([templateVariableSchema, schema]);
    }
    // No template variables - return original schema for full validation
    return schema;
}
// ============================================================================
// END INPUT-AWARE TEMPLATE VARIABLE VALIDATION
// ============================================================================
/**
 * Validate arguments against a schema while allowing template variables
 * @param argsSchema - The Zod schema to validate against
 * @param args - The arguments to validate
 * @returns Validation result with success flag and any issues
 */
function validateArgsWithVariables(argsSchema, args) {
    // Log validation attempt for debugging
    console.log('[Validation] Schema type:', argsSchema.constructor.name);
    console.log('[Validation] Args type:', Array.isArray(args) ? 'array' : typeof args);
    console.log('[Validation] Args value:', JSON.stringify(args, null, 2));
    try {
        // Use input-aware schema relaxation - only relaxes fields with template variables
        var relaxedSchema = relaxSchemaForTemplateVariables(argsSchema, args);
        // Validate using the relaxed schema
        var result = relaxedSchema.safeParse(args);
        if (result.success) {
            console.log('[Validation] Success');
            return { success: true, issues: [] };
        }
        // Log detailed error information
        console.log('[Validation] Failed with errors:', result.error.errors);
        console.log('[Validation] Relaxed schema type:', relaxedSchema.constructor.name);
        // For union errors, log details of each option's failure
        if (result.error.errors.length > 0) {
            var firstError = result.error.errors[0];
            console.log('[Validation] First error code:', firstError.code);
            console.log('[Validation] First error keys:', Object.keys(firstError));
            if (firstError.code === 'invalid_union') {
                console.log('[Validation] This is a union error');
                if ('unionErrors' in firstError && Array.isArray(firstError.unionErrors)) {
                    console.log('[Validation] Union has', firstError.unionErrors.length, 'options:');
                    firstError.unionErrors.forEach(function (err, idx) {
                        if (err && err.errors) {
                            console.log("[Validation]   Option ".concat(idx + 1, " (").concat(err.errors.length, " errors):"));
                            err.errors.forEach(function (e, i) {
                                console.log("[Validation]     Error ".concat(i + 1, ":"), {
                                    code: e.code,
                                    path: e.path,
                                    message: e.message,
                                    received: e.received,
                                });
                            });
                        }
                    });
                }
                else {
                    console.log('[Validation] No unionErrors found in error object');
                }
            }
        }
        // For debugging union issues, also try validating against the original schema
        var originalResult = argsSchema.safeParse(args);
        if (originalResult.success) {
            console.log('[Validation] WARNING: Original schema passes but relaxed schema fails!');
            console.log('[Validation] This indicates an issue with template variable relaxation');
            return { success: true, issues: [] };
        }
        else {
            console.log('[Validation] Original schema also fails:', originalResult.error.errors);
        }
        // Convert Zod errors to WorkflowValidationIssue format
        var issues = result.error.errors.map(function (error) {
            var path = error.path.join('.');
            var actualValue = getValueAtPath(args, error.path);
            var receivedType = Array.isArray(actualValue) ? 'array' : typeof actualValue;
            var expectedType = 'expected' in error ? error.expected : 'unknown';
            console.log("[Validation] Error at path \"".concat(path, "\": Expected ").concat(expectedType, ", received ").concat(receivedType));
            console.log("[Validation] Actual value at path: ".concat(JSON.stringify(actualValue)));
            // Create a more descriptive error message
            var getDescriptiveMessage = function (error, path, actualValue) {
                var _a;
                var pathDesc = path ? "\"".concat(path, "\"") : 'at root';
                // Handle union errors with detailed breakdown
                if (error.code === 'invalid_union') {
                    var actualType = Array.isArray(actualValue) ? 'array' : typeof actualValue;
                    var message = "Parameter ".concat(pathDesc, ": Union validation failed. Expected one of multiple types, received ").concat(actualType, ".");
                    // Try to extract union error details
                    if (error.unionErrors && Array.isArray(error.unionErrors)) {
                        var errorSummaries = error.unionErrors
                            .slice(0, 3) // Limit to first 3 options for brevity
                            .map(function (unionError, idx) {
                            var _a, _b;
                            if (unionError && unionError.errors && unionError.errors[0]) {
                                var firstError = unionError.errors[0];
                                if (firstError.code === 'invalid_type') {
                                    return "Option ".concat(idx + 1, ": Expected ").concat(firstError.expected || 'unknown');
                                }
                                else if (firstError.code === 'invalid_union_discriminator') {
                                    return "Option ".concat(idx + 1, ": Discriminator field \"").concat(((_a = firstError.path) === null || _a === void 0 ? void 0 : _a.join('.')) || 'type', "\" must be one of: ").concat(((_b = firstError.options) === null || _b === void 0 ? void 0 : _b.slice(0, 5).join(', ')) || 'specific values');
                                }
                                return "Option ".concat(idx + 1, ": ").concat(firstError.message || 'validation failed');
                            }
                            return "Option ".concat(idx + 1, ": validation failed");
                        })
                            .filter(Boolean);
                        if (errorSummaries.length > 0) {
                            message += '\n  ' + errorSummaries.join('\n  ');
                        }
                    }
                    message += "\nReceived value: ".concat((_a = JSON.stringify(actualValue)) === null || _a === void 0 ? void 0 : _a.substring(0, 200));
                    return message;
                }
                // Handle discriminated union errors specifically
                if (error.code === 'invalid_union_discriminator') {
                    var options = error.options ? error.options.slice(0, 10).join(' | ') : 'specific values';
                    return "Parameter ".concat(pathDesc, ": Invalid discriminator value. Expected one of: ").concat(options, ". Received: ").concat(JSON.stringify(actualValue));
                }
                // Handle specific error types with more detail
                if (error.code === 'invalid_type') {
                    var actualType = Array.isArray(actualValue) ? 'array' : typeof actualValue;
                    var expectedType_1 = 'expected' in error ? error.expected : 'unknown type';
                    // Add tool-specific guidance for common cases
                    var guidance = '';
                    if (path.includes('sourceType') && expectedType_1 === 'object') {
                        guidance =
                            '. Use: {"type": "raw", "data": "{{yourData}}"} or {"type": "url", "url": "{{dataUrl}}"}';
                    }
                    else if (path.includes('inputs') && expectedType_1 === 'object') {
                        guidance = '. Inputs should be key-value pairs for tool parameters';
                    }
                    else if (path.includes('edges') && expectedType_1 === 'array') {
                        guidance =
                            '. Use: [{"tag": "success", "target": "nextNode"}] or [{"tag": "done", "terminal": "success"}]';
                    }
                    return "Parameter ".concat(pathDesc, ": Expected ").concat(expectedType_1, ", received ").concat(actualType).concat(guidance);
                }
                if (error.code === 'invalid_string') {
                    // Check if this might be a template variable that failed URL/email validation
                    var stringValue = typeof actualValue === 'string' ? actualValue : '';
                    if (stringValue.includes('{{') && stringValue.includes('}}')) {
                        return "Parameter ".concat(pathDesc, ": Template variable \"").concat(stringValue, "\" failed format validation (").concat(error.validation || error.message, "). This may be expected if the template resolves to the correct format at runtime.");
                    }
                    return "Parameter ".concat(pathDesc, ": Invalid string format - ").concat(error.validation || error.message);
                }
                if (error.code === 'invalid_enum_value') {
                    var expectedValues = error.options ? error.options.join(', ') : 'valid enum value';
                    return "Parameter ".concat(pathDesc, ": Expected one of [").concat(expectedValues, "], received \"").concat(actualValue, "\"");
                }
                if (error.code === 'custom') {
                    return "Parameter ".concat(pathDesc, ": ").concat(error.message);
                }
                // For generic "Invalid input" messages, try to be more specific
                if (error.message === 'Invalid input' || error.message === 'Required') {
                    if (actualValue === undefined || actualValue === null) {
                        var example = '';
                        if (path.includes('name')) {
                            example = '. Example: "Process Data"';
                        }
                        else if (path.includes('description')) {
                            example = '. Example: "Processes the uploaded CSV file"';
                        }
                        else if (path.includes('start')) {
                            example = '. Use the ID of your first node';
                        }
                        return "Parameter ".concat(pathDesc, ": Required parameter is missing").concat(example);
                    }
                    var actualType = Array.isArray(actualValue) ? 'array' : typeof actualValue;
                    var expectedType_2 = ('expected' in error ? error.expected : undefined) || 'valid input';
                    return "Parameter ".concat(pathDesc, ": Expected ").concat(expectedType_2, ", received ").concat(actualType, " (value: ").concat(JSON.stringify(actualValue), ")");
                }
                // Fallback to original message but with more context
                return "Parameter ".concat(pathDesc, ": ").concat(error.message, " (received: ").concat(JSON.stringify(actualValue), ")");
            };
            return {
                type: 'invalid_parameter',
                parameter: path || undefined,
                expectedType: (('expected' in error ? error.expected : undefined) ||
                    error.code ||
                    undefined),
                actualValue: actualValue,
                message: getDescriptiveMessage(error, path, actualValue),
            };
        });
        return { success: false, issues: issues };
    }
    catch (error) {
        // Handle errors in schema transformation
        console.log('[Validation] Error in allowTemplateVariables:', error);
        return {
            success: false,
            issues: [
                {
                    type: 'validation_error',
                    message: "Validation error: ".concat(error instanceof Error ? error.message : 'Unknown error in schema transformation'),
                },
            ],
        };
    }
}
/**
 * Validate that edge conditions reference available variables
 * @param node - Workflow node with edges to validate
 * @param workflowParams - Available workflow parameters
 * @param availableNodeOutputs - Set of node IDs that produce outputs before this node
 * @returns Validation issues for missing edge condition variables
 */
function validateEdgeConditionVariables(node, workflowParams, availableNodeOutputs) {
    if (workflowParams === void 0) { workflowParams = {}; }
    if (availableNodeOutputs === void 0) { availableNodeOutputs = new Set(); }
    var issues = [];
    if (!node.edges || !Array.isArray(node.edges)) {
        return issues;
    }
    // Router nodes are structural no-ops - they don't provide variables
    // Get available variables from workflow parameters
    var workflowParamVars = new Set(Object.keys(workflowParams));
    for (var i = 0; i < node.edges.length; i++) {
        var edge = node.edges[i];
        if (!edge.condition || typeof edge.condition !== 'string') {
            continue; // Skip edges without conditions
        }
        // Extract all template variables from the condition
        var templateVars = extractTemplateVariables(edge.condition);
        var _loop_1 = function (varPath) {
            var rootVar = varPath.split('.')[0]; // Get the root variable name (before any dots)
            var isAvailable = false;
            // Check if variable is available from workflow parameters
            if (workflowParamVars.has(rootVar)) {
                isAvailable = true;
            }
            // Check if variable references a node output
            else if (availableNodeOutputs.has(rootVar)) {
                isAvailable = true;
            }
            if (!isAvailable) {
                // Create suggestions for similar variable names
                var allAvailableVars = __spreadArray(__spreadArray([], Array.from(workflowParamVars), true), Array.from(availableNodeOutputs), true);
                var suggestions = allAvailableVars.filter(function (v) {
                    return v.toLowerCase().includes(rootVar.toLowerCase()) ||
                        rootVar.toLowerCase().includes(v.toLowerCase());
                });
                var suggestionText = suggestions.length > 0 ? " Did you mean: ".concat(suggestions.join(', '), "?") : '';
                issues.push({
                    type: 'missing_edge_condition_variable',
                    nodeId: node.id,
                    edgeTag: edge.tag,
                    message: "Edge condition \"".concat(edge.condition, "\" references undefined variable \"{{").concat(varPath, "}}\". ") +
                        "Available variables: workflow parameters [".concat(Array.from(workflowParamVars).join(', ') || 'none', "], ") +
                        "node outputs [".concat(Array.from(availableNodeOutputs).join(', ') || 'none', "].").concat(suggestionText),
                });
            }
        };
        for (var _i = 0, templateVars_1 = templateVars; _i < templateVars_1.length; _i++) {
            var varPath = templateVars_1[_i];
            _loop_1(varPath);
        }
    }
    return issues;
}
// validateRouterVariableConsistency removed - router nodes are now structural no-ops
// Edge condition validation is handled at the workflow level using existing variables
/**
 * Build a comprehensive variable registry for the entire workflow
 * This analyzes all nodes, their dependencies, execution order, and available variables
 */
function buildWorkflowVariableRegistry(workflow) {
    var _a;
    var registry = {
        workflowParameters: new Set(),
        nodeVariables: new Map(),
        variablePaths: new Map(),
        executionOrder: new Map(),
        nodeDependencies: new Map(),
    };
    // Extract workflow parameters
    if ((_a = workflow.parameters) === null || _a === void 0 ? void 0 : _a.properties) {
        Object.keys(workflow.parameters.properties).forEach(function (param) {
            registry.workflowParameters.add(param);
            registry.variablePaths.set(param, new Set([param]));
        });
    }
    // Build node dependency graph by analyzing template variable references in inputs and edge conditions
    var nodeDependencies = new Map();
    var nodeIds = new Set(workflow.nodes.map(function (n) { return n.id; }));
    for (var _i = 0, _b = workflow.nodes; _i < _b.length; _i++) {
        var node = _b[_i];
        var dependencies = new Set();
        // Analyze node inputs for template variables
        var inputStr = JSON.stringify(node.inputs || {});
        var inputVariables = extractTemplateVariables(inputStr);
        // Analyze edge conditions for template variables
        var edgeConditions = (node.edges || [])
            .filter(function (edge) { return edge.condition; })
            .map(function (edge) { return edge.condition; })
            .join(' ');
        var edgeVariables = extractTemplateVariables(edgeConditions);
        // Combine all variables referenced by this node
        var allReferencedVars = __spreadArray(__spreadArray([], inputVariables, true), edgeVariables, true);
        for (var _c = 0, allReferencedVars_1 = allReferencedVars; _c < allReferencedVars_1.length; _c++) {
            var varPath = allReferencedVars_1[_c];
            var rootVar = varPath.split('.')[0];
            // If this references another node's output, add as dependency
            if (nodeIds.has(rootVar) && rootVar !== node.id) {
                dependencies.add(rootVar);
            }
        }
        nodeDependencies.set(node.id, dependencies);
    }
    registry.nodeDependencies = nodeDependencies;
    // Calculate execution order using topological sort
    var executionOrder = calculateExecutionOrder(workflow.nodes, nodeDependencies);
    registry.executionOrder = executionOrder;
    // Extract variables that each node can produce
    // For now, we'll use basic inference - each node produces variables named after its nodeId
    // This will be enhanced when we integrate tool output schema extraction
    for (var _d = 0, _e = workflow.nodes; _d < _e.length; _d++) {
        var node = _e[_d];
        var nodeVariables = new Set();
        // Every node produces variables under its own nodeId
        nodeVariables.add(node.id);
        // Router nodes are structural no-ops - they don't produce additional variables
        // Only the node ID itself is available (standard behavior for all nodes)
        registry.nodeVariables.set(node.id, nodeVariables);
        // Add the node itself as a root variable path
        registry.variablePaths.set(node.id, new Set([node.id]));
    }
    return registry;
}
/**
 * Calculate execution order using topological sort
 */
function calculateExecutionOrder(nodes, dependencies) {
    var executionOrder = new Map();
    var visited = new Set();
    var visiting = new Set();
    var order = 0;
    // Topological sort with cycle detection
    function visit(nodeId) {
        if (visiting.has(nodeId)) {
            console.warn("[VALIDATION] Circular dependency detected involving node: ".concat(nodeId));
            return;
        }
        if (visited.has(nodeId)) {
            return;
        }
        visiting.add(nodeId);
        var deps = dependencies.get(nodeId) || new Set();
        for (var _i = 0, _a = Array.from(deps); _i < _a.length; _i++) {
            var dep = _a[_i];
            visit(dep);
        }
        visiting.delete(nodeId);
        visited.add(nodeId);
        executionOrder.set(nodeId, order++);
    }
    // Visit all nodes
    for (var _i = 0, nodes_1 = nodes; _i < nodes_1.length; _i++) {
        var node = nodes_1[_i];
        visit(node.id);
    }
    return executionOrder;
}
/**
 * Get all variables available at a specific node during execution
 * @param nodeId - The node to check variable availability for
 * @param registry - The workflow variable registry
 * @returns Set of available variable root names
 */
function getAvailableVariablesAtNode(nodeId, registry) {
    var _a;
    var availableVars = new Set();
    // Always include workflow parameters
    Array.from(registry.workflowParameters).forEach(function (param) { return availableVars.add(param); });
    // Include variables from nodes that execute before this node
    var currentOrder = (_a = registry.executionOrder.get(nodeId)) !== null && _a !== void 0 ? _a : Infinity;
    Array.from(registry.executionOrder.entries()).forEach(function (_a) {
        var otherNodeId = _a[0], otherOrder = _a[1];
        if (otherOrder < currentOrder) {
            // This node executes before the current node, so its variables are available
            var nodeVars = registry.nodeVariables.get(otherNodeId) || new Set();
            Array.from(nodeVars).forEach(function (varName) { return availableVars.add(varName); });
        }
    });
    return availableVars;
}
/**
 * Extract variable paths from a Zod schema
 * This analyzes the schema structure to determine what variables a node can produce
 * @param schema - The Zod schema to analyze
 * @param prefix - Current path prefix for nested analysis
 * @returns Set of variable paths that can be produced by this schema
 */
function extractVariablePathsFromSchema(schema, prefix) {
    if (prefix === void 0) { prefix = ''; }
    var paths = new Set();
    var currentPath = prefix;
    try {
        // Add the current path (for root level access)
        if (currentPath) {
            paths.add(currentPath);
        }
        // Handle different Zod schema types
        if (schema instanceof zod_1.z.ZodObject) {
            var shape = schema.shape;
            for (var _i = 0, _a = Object.entries(shape); _i < _a.length; _i++) {
                var _b = _a[_i], key = _b[0], value = _b[1];
                var newPath = currentPath ? "".concat(currentPath, ".").concat(key) : key;
                paths.add(newPath);
                // Recursively extract paths from nested objects
                var nestedPaths = extractVariablePathsFromSchema(value, newPath);
                Array.from(nestedPaths).forEach(function (path) { return paths.add(path); });
            }
        }
        else if (schema instanceof zod_1.z.ZodArray) {
            // For arrays, we can access elements by index and also map properties
            if (currentPath) {
                // Array indexing: someArray[0], someArray[1], etc.
                paths.add("".concat(currentPath, "[0]"));
                paths.add("".concat(currentPath, "[*]")); // Symbolic representation for any index
                // Array property mapping: if array contains objects, we can access their properties
                if (schema._def && schema._def.type) {
                    var elementPaths = extractVariablePathsFromSchema(schema._def.type, "".concat(currentPath, "[*]"));
                    Array.from(elementPaths).forEach(function (path) {
                        // Convert someArray[*].field to someArray.field (property mapping syntax)
                        var mappedPath = path.replace('[*].', '.');
                        if (mappedPath !== path) {
                            paths.add(mappedPath);
                        }
                        paths.add(path);
                    });
                }
            }
        }
        else if (schema instanceof zod_1.z.ZodRecord) {
            // For records, we can access any key
            if (currentPath) {
                paths.add("".concat(currentPath, ".*")); // Symbolic representation for any key
                // Recursively extract paths from record values
                if (schema._def && schema._def.valueType) {
                    var valuePaths = extractVariablePathsFromSchema(schema._def.valueType, "".concat(currentPath, ".*"));
                    Array.from(valuePaths).forEach(function (path) { return paths.add(path); });
                }
            }
        }
        else if (schema instanceof zod_1.z.ZodUnion) {
            // For unions, extract paths from all possible types
            for (var _c = 0, _d = schema.options; _c < _d.length; _c++) {
                var option = _d[_c];
                var unionPaths = extractVariablePathsFromSchema(option, currentPath);
                Array.from(unionPaths).forEach(function (path) { return paths.add(path); });
            }
        }
        else if (schema instanceof zod_1.z.ZodOptional || schema instanceof zod_1.z.ZodNullable) {
            // For optional/nullable types, extract paths from the wrapped type
            var wrappedPaths = extractVariablePathsFromSchema(schema.unwrap(), currentPath);
            Array.from(wrappedPaths).forEach(function (path) { return paths.add(path); });
        }
        // For other types (primitives, etc.), just add the current path if it exists
        if (currentPath && !paths.has(currentPath)) {
            paths.add(currentPath);
        }
    }
    catch (error) {
        console.warn("[VALIDATION] Error extracting paths from schema:", error);
        // Fallback: add just the current path
        if (currentPath) {
            paths.add(currentPath);
        }
    }
    return paths;
}
/**
 * Get variable paths that a specific tool can produce based on its output schema
 * @param toolId - The tool identifier
 * @param toolMetadata - Tool metadata containing output schema information
 * @returns Set of variable paths this tool can produce
 */
function getToolOutputVariables(toolId, toolMetadata) {
    var variables = new Set();
    try {
        // Special handling for router nodes - they are structural no-ops
        if (toolId === 'nativeRouter') {
            // Router nodes don't produce variables - they're structural only
            // Only the node ID itself is available (standard behavior)
            return variables; // Return empty set, standard node behavior will add node ID
        }
        if (!toolMetadata) {
            console.warn("[VALIDATION] No metadata available for tool: ".concat(toolId));
            // Fallback: assume the tool produces a variable named after itself
            variables.add(toolId);
            return variables;
        }
        // Extract schema from tool metadata
        var resultSchema 
        // Handle different metadata structures
        = void 0;
        // Handle different metadata structures
        if (toolMetadata.resultSchema) {
            if (typeof toolMetadata.resultSchema === 'function') {
                // Dynamic schema - we'll use basic inference for now
                variables.add('result');
                variables.add('data');
            }
            else if (toolMetadata.resultSchema instanceof zod_1.z.ZodType) {
                resultSchema = toolMetadata.resultSchema;
            }
            else if (toolMetadata.resultSchema.single) {
                // Handle emitMode-aware schemas
                resultSchema = toolMetadata.resultSchema.single;
            }
        }
        if (resultSchema) {
            // Special case: z.any() schemas (like router and nativeLlmResponseText) produce dynamic output
            if (resultSchema instanceof zod_1.z.ZodAny) {
                variables.add('*'); // Indicates dynamic variables
                return variables;
            }
            var paths = extractVariablePathsFromSchema(resultSchema);
            Array.from(paths).forEach(function (path) { return variables.add(path); });
        }
        else {
            // Fallback: add common variable patterns
            variables.add('result');
            variables.add('data');
            variables.add('output');
        }
    }
    catch (error) {
        console.warn("[VALIDATION] Error extracting output variables for tool ".concat(toolId, ":"), error);
        // Fallback
        variables.add('result');
    }
    return variables;
}
/**
 * Enhanced workflow variable registry builder with tool schema integration
 * @param workflow - The workflow configuration
 * @param toolMetadata - Optional tool metadata map for schema extraction
 * @returns Enhanced variable registry with tool-specific variable paths
 */
function buildEnhancedWorkflowVariableRegistry(workflow, toolMetadata) {
    // Start with the basic registry
    var registry = buildWorkflowVariableRegistry(workflow);
    // Enhance with tool-specific variable extraction
    if (toolMetadata) {
        var _loop_2 = function (node) {
            var metadata = toolMetadata.get(node.toolId);
            if (metadata) {
                var toolVariables = getToolOutputVariables(node.toolId, metadata);
                // Add tool-specific variables to the node's variable set
                var existingVariables_1 = registry.nodeVariables.get(node.id) || new Set();
                Array.from(toolVariables).forEach(function (varPath) {
                    existingVariables_1.add(varPath);
                    // Also add to variable paths registry
                    var rootVar = varPath.split('.')[0];
                    if (!registry.variablePaths.has(rootVar)) {
                        registry.variablePaths.set(rootVar, new Set());
                    }
                    registry.variablePaths.get(rootVar).add(varPath);
                });
                registry.nodeVariables.set(node.id, existingVariables_1);
            }
        };
        for (var _i = 0, _a = workflow.nodes; _i < _a.length; _i++) {
            var node = _a[_i];
            _loop_2(node);
        }
    }
    return registry;
}
/**
 * Validate all template variables in a workflow using comprehensive context
 * This is the main entry point for workflow variable validation
 * @param workflow - The workflow configuration to validate
 * @param toolMetadata - Optional tool metadata for enhanced validation
 * @returns Array of validation issues found
 */
function validateWorkflowVariables(workflow, toolMetadata) {
    var issues = [];
    // Build comprehensive variable registry
    var registry = buildEnhancedWorkflowVariableRegistry(workflow, toolMetadata);
    // Validate each node's inputs and edge conditions
    for (var _i = 0, _a = workflow.nodes; _i < _a.length; _i++) {
        var node = _a[_i];
        // Get variables available at this node
        var availableVars = getAvailableVariablesAtNode(node.id, registry);
        // Validate node input template variables
        var inputIssues = validateNodeInputVariables(node, availableVars, registry);
        issues.push.apply(issues, inputIssues);
        // Validate edge condition template variables
        var edgeIssues = validateNodeEdgeConditionVariables(node, availableVars, registry);
        issues.push.apply(issues, edgeIssues);
    }
    return issues;
}
/**
 * Validate template variables in node inputs
 * @param node - The node to validate
 * @param availableVars - Set of variables available at this node
 * @param registry - The workflow variable registry
 * @returns Array of validation issues
 */
function validateNodeInputVariables(node, availableVars, registry) {
    var issues = [];
    if (!node.inputs) {
        return issues;
    }
    // Convert inputs to string and extract all template variables
    var inputStr = JSON.stringify(node.inputs);
    var templateVars = extractTemplateVariables(inputStr);
    for (var _i = 0, templateVars_2 = templateVars; _i < templateVars_2.length; _i++) {
        var varPath = templateVars_2[_i];
        var issue = validateVariablePath(varPath, availableVars, registry, {
            nodeId: node.id,
            context: 'node_input',
            inputField: findInputFieldForVariable(node.inputs, varPath),
        });
        if (issue) {
            issues.push(issue);
        }
    }
    return issues;
}
/**
 * Enhanced edge condition validation with full workflow context
 * @param node - The node to validate
 * @param availableVars - Set of variables available at this node
 * @param registry - The workflow variable registry
 * @returns Array of validation issues
 */
function validateNodeEdgeConditionVariables(node, availableVars, registry) {
    var issues = [];
    if (!node.edges || !Array.isArray(node.edges)) {
        return issues;
    }
    for (var _i = 0, _a = node.edges; _i < _a.length; _i++) {
        var edge = _a[_i];
        if (!edge.condition || typeof edge.condition !== 'string') {
            continue;
        }
        var templateVars = extractTemplateVariables(edge.condition);
        for (var _b = 0, templateVars_3 = templateVars; _b < templateVars_3.length; _b++) {
            var varPath = templateVars_3[_b];
            var issue = validateVariablePath(varPath, availableVars, registry, {
                nodeId: node.id,
                edgeTag: edge.tag,
                context: 'edge_condition',
                condition: edge.condition,
            });
            if (issue) {
                issues.push(issue);
            }
        }
    }
    return issues;
}
/**
 * Validate a specific variable path against available variables
 * @param varPath - The variable path to validate (e.g., "nodeId.field.subfield")
 * @param availableVars - Set of available root variables
 * @param registry - The workflow variable registry
 * @param context - Context information for error reporting
 * @returns Validation issue if the variable path is invalid, null otherwise
 */
function validateVariablePath(varPath, availableVars, registry, context) {
    var rootVar = varPath.split('.')[0];
    // Check if root variable is available
    if (!availableVars.has(rootVar)) {
        // Generate suggestions for similar variables
        var allVars = Array.from(availableVars);
        var suggestions = allVars.filter(function (v) {
            return v.toLowerCase().includes(rootVar.toLowerCase()) ||
                rootVar.toLowerCase().includes(v.toLowerCase());
        });
        var suggestionText = suggestions.length > 0 ? " Did you mean: ".concat(suggestions.join(', '), "?") : '';
        var contextDescription = context.context === 'edge_condition'
            ? "edge condition \"".concat(context.condition, "\"")
            : "input field \"".concat(context.inputField || 'unknown', "\"");
        return {
            type: 'missing_variable',
            nodeId: context.nodeId,
            edgeTag: context.edgeTag,
            message: "".concat(context.nodeId, " ").concat(contextDescription, " references undefined variable \"{{").concat(varPath, "}}\". ") +
                "Available variables: [".concat(Array.from(availableVars).join(', ') || 'none', "].").concat(suggestionText),
        };
    }
    // For more complex path validation (nested properties), we can check against variablePaths
    var availablePaths = registry.variablePaths.get(rootVar);
    if (availablePaths && varPath.includes('.')) {
        var pathExists = availablePaths.has(varPath) ||
            availablePaths.has(varPath.replace(/\[\d+\]/g, '[*]')) || // Handle array indexing
            Array.from(availablePaths).some(function (path) { return path.includes('*'); }); // Handle dynamic paths
        if (!pathExists) {
            var pathSuggestions = Array.from(availablePaths).filter(function (path) { return path.toLowerCase().includes(varPath.toLowerCase()) || path.startsWith(rootVar); });
            var suggestionText = pathSuggestions.length > 0
                ? " Available paths for ".concat(rootVar, ": ").concat(pathSuggestions.join(', '), ".")
                : '';
            var contextDescription = context.context === 'edge_condition'
                ? "edge condition \"".concat(context.condition, "\"")
                : "input field \"".concat(context.inputField || 'unknown', "\"");
            return {
                type: 'invalid_variable_path',
                nodeId: context.nodeId,
                edgeTag: context.edgeTag,
                message: "".concat(context.nodeId, " ").concat(contextDescription, " references path \"{{").concat(varPath, "}}\" which may not exist.").concat(suggestionText),
            };
        }
    }
    return null; // Variable path is valid
}
/**
 * Find which input field contains a specific template variable
 * This helps provide better error messages by identifying the exact field with the issue
 */
function findInputFieldForVariable(inputs, varPath) {
    function searchObject(obj, currentPath) {
        if (currentPath === void 0) { currentPath = ''; }
        if (typeof obj === 'string' && obj.includes("{{".concat(varPath, "}}"))) {
            return currentPath;
        }
        if (typeof obj === 'object' && obj !== null) {
            for (var _i = 0, _a = Object.entries(obj); _i < _a.length; _i++) {
                var _b = _a[_i], key = _b[0], value = _b[1];
                var newPath = currentPath ? "".concat(currentPath, ".").concat(key) : key;
                var result = searchObject(value, newPath);
                if (result) {
                    return result;
                }
            }
        }
        return undefined;
    }
    return searchObject(inputs);
}
/**
 * Test the comprehensive validation system with the original problematic workflow
 * This demonstrates how the enhanced validation would catch the router variable mismatch issue
 */
function testComprehensiveValidation(workflowJson) {
    console.log('[VALIDATION-TEST] Testing comprehensive validation system...');
    // Build the enhanced variable registry
    var registry = buildEnhancedWorkflowVariableRegistry(workflowJson);
    console.log('[VALIDATION-TEST] Variable registry built:', {
        workflowParameters: Array.from(registry.workflowParameters),
        nodeCount: registry.nodeVariables.size,
        executionOrder: Array.from(registry.executionOrder.entries()),
        nodeDependencies: Array.from(registry.nodeDependencies.entries()).map(function (_a) {
            var nodeId = _a[0], deps = _a[1];
            return ({
                nodeId: nodeId,
                dependencies: Array.from(deps),
            });
        }),
    });
    // Run comprehensive validation
    var issues = validateWorkflowVariables(workflowJson);
    // Router nodes are structural no-ops - no specific validation needed
    // Edge condition validation handled by general workflow validation
    var allIssues = __spreadArray([], issues, true);
    // Categorize issues
    var summary = {
        totalIssues: allIssues.length,
        missingVariables: allIssues.filter(function (issue) { return issue.type === 'missing_variable'; }).length,
        invalidPaths: allIssues.filter(function (issue) { return issue.type === 'invalid_variable_path'; }).length,
        routerMismatches: allIssues.filter(function (issue) { return issue.type === 'router_variable_mismatch'; }).length,
    };
    console.log('[VALIDATION-TEST] Validation completed:', summary);
    if (allIssues.length > 0) {
        console.log('[VALIDATION-TEST] Issues found:');
        allIssues.forEach(function (issue, index) {
            console.log("  ".concat(index + 1, ". [").concat(issue.type, "] ").concat(issue.message));
        });
    }
    else {
        console.log('[VALIDATION-TEST] No validation issues found!');
    }
    return {
        issues: allIssues,
        registry: registry,
        summary: summary,
    };
}
